import type { User } from '@hillchart/shared';
import { queryOptions } from '@tanstack/react-query';
import { ApiError, authApi } from './api';
import { queryKeys } from './query-keys';

/**
 * Shared query definition for `/auth/me`.
 *
 * Exposed as a factory returning `queryOptions` so both `useQuery` (in
 * `useSession`) and `queryClient.ensureQueryData` (in TanStack Router
 * `beforeLoad` guards) consume the exact same configuration — queryKey,
 * queryFn, staleTime, retry policy — without drift.
 *
 * A 401 from the backend is mapped to `null` rather than being thrown:
 * "not logged in" is a valid business state, not an error state, so route
 * guards can branch on `session === null` instead of inspecting errors.
 */
export const sessionQueryOptions = () =>
  queryOptions({
    queryKey: queryKeys.session(),
    queryFn: async (): Promise<User | null> => {
      try {
        return await authApi.me();
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
      if (error instanceof ApiError) return false;
      return failureCount < 1;
    },
  });
